/* Iter v34s (#1) — Karyawan Edit Profil (ajukan perubahan, butuh approval admin) */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, PageHeader, Button, Field, Input, Badge } from "@/components/ui-kit";
import { AlertCircle, Mail, ShieldCheck, Clock, XCircle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { DatePicker } from "@/components/DatePicker";

export default function KaryawanEditProfil() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [pending, setPending] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "",
    ktp_number: "", place_of_birth: "", date_of_birth: "",
    emergency_contact: "", bank_name: "", bank_account_number: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/employees/me/profile");
      setMe(r.data);
      setForm((f) => ({
        ...f,
        name: r.data.name || "", email: r.data.email || "",
        phone: r.data.phone || "", address: r.data.address || "",
        ktp_number: r.data.ktp_number || r.data.nik || "",
        place_of_birth: r.data.place_of_birth || "",
        date_of_birth: r.data.date_of_birth || r.data.birth_date || "",
        emergency_contact: r.data.emergency_contact || "",
        bank_name: r.data.bank_name || "",
        bank_account_number: r.data.bank_account_number || "",
      }));
    } catch (_e) { /* ignore */ }
    try {
      const p = await api.get("/profile-change/my");
      setPending(p.data?.status === "none" ? null : p.data);
    } catch (_e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      // Only send changed fields
      const patch = {};
      Object.entries(form).forEach(([k, v]) => {
        const orig = (me?.[k] ?? me?.[k === "ktp_number" ? "nik" : k === "date_of_birth" ? "birth_date" : k]) || "";
        if ((v || "").trim() !== String(orig || "").trim()) patch[k] = v;
      });
      if (!Object.keys(patch).length) {
        toast.info("Tidak ada perubahan");
        setSubmitting(false);
        return;
      }
      const r = await api.post("/profile-change/request", patch);
      if (r.data?.email_changed) {
        toast.success("Permintaan dibuat. Cek email BARU untuk verifikasi sebelum disetujui admin.");
      } else {
        toast.success("Permintaan dikirim ke admin untuk persetujuan.");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim permintaan");
    } finally { setSubmitting(false); }
  };

  if (!me) return <div className="p-8 text-stone-400">Memuat...</div>;

  return (
    <div className="p-6 space-y-4 max-w-3xl mx-auto" data-testid="karyawan-edit-profil">
      <PageHeader title="Edit Profil Saya" subtitle="Ajukan perubahan data — butuh persetujuan admin. Perubahan email butuh verifikasi link." />

      {pending && (
        <Card className={`p-4 border-l-4 ${pending.status === "pending_admin" ? "border-l-amber-500 bg-amber-50/30" : "border-l-blue-500 bg-blue-50/30"}`} data-testid="pending-request-banner">
          <div className="flex gap-3">
            {pending.status === "pending_admin" ? <Clock className="h-5 w-5 text-amber-600 mt-0.5" /> : <Mail className="h-5 w-5 text-blue-600 mt-0.5" />}
            <div className="flex-1">
              <p className="font-semibold text-sm">
                {pending.status === "pending_admin" ? "Menunggu Persetujuan Admin" : "Menunggu Verifikasi Email Baru"}
              </p>
              <p className="text-xs text-stone-600 mt-1">
                Diajukan: {pending.submitted_at?.slice(0, 19).replace("T", " ")}<br />
                Perubahan: {Object.keys(pending.patch || {}).join(", ")}
              </p>
              {pending.verify_url && (
                <p className="text-xs text-stone-700 mt-2">
                  Link verifikasi: <a href={pending.verify_url} className="text-terra-700 underline">Klik di sini</a>
                </p>
              )}
            </div>
          </div>
        </Card>
      )}

      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Nama Lengkap"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" /></Field>
          <Field label="Email (perubahan butuh verifikasi)">
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-email" />
          </Field>
          <Field label="No HP"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-phone" /></Field>
          <Field label="No KTP / NIK"><Input value={form.ktp_number} onChange={(e) => setForm({ ...form, ktp_number: e.target.value })} data-testid="edit-ktp" /></Field>
          <Field label="Tempat Lahir"><Input value={form.place_of_birth} onChange={(e) => setForm({ ...form, place_of_birth: e.target.value })} data-testid="edit-pob" /></Field>
          <Field label="Tanggal Lahir"><DatePicker value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="edit-dob" /></Field>
          <Field label="Kontak Darurat"><Input value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} data-testid="edit-emergency" /></Field>
          <Field label="Nama Bank"><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="edit-bank-name" /></Field>
          <Field label="No Rekening"><Input value={form.bank_account_number} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} data-testid="edit-bank-acc" /></Field>
          <div className="md:col-span-2">
            <Field label="Alamat"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="edit-address" /></Field>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-stone-200">
          <Button variant="ghost" onClick={() => nav(-1)}>Batal</Button>
          <Button onClick={submit} disabled={submitting || !!pending} data-testid="submit-change-request">
            {pending ? "Sudah ada pengajuan" : (submitting ? "Mengirim..." : "Ajukan Perubahan")}
          </Button>
        </div>
      </Card>
    </div>
  );
}
